<template>
    <div class="form-container">
        <AddressField
            :name="name"
            :label="label"
            :searchName="searchName"
            searchField="StreetAddress"
            :searchLabel="searchLabel"
            :addressFields="addressFields"
            :addressType="addressType"
            :customErrorMessage="errorMessage"
            :validationRules="validationRules"
            :searchType="'predictive'"
        />
    </div>
</template>

<script>
import { useStore } from 'vuex'

export default {
    // static settings to initialize step (not reactive inside this file!)
    stepDefinition() {
        // you can have multiple steps use the same file if you return an array
        return [
            {
                name: 'currentAddress',
                hideContinue: true,
                stringOnSummary: true,
                selected: null,
                manual: false,
                supportsSearch: true,
                route: {
                    relativeUrl: `current-address`,
                    name: 'apply.address.currentAddress',
                    props: { addressType: 'current', formSection: 'address' },
                    meta: {
                        title: 'form.sections.address.currentAddress.heading',
                        customTooltip: true,
                    },
                },
                fields: {
                    currentAddressBuildingNumber: {
                        value: null,
                        mappedKey: 'address2',
                        validationRules: {
                            max: 50,
                        },
                    },
                    currentAddressStreetAddress: {
                        value: null,
                        mappedKey: 'address1',
                        legacyMappedKey: 'address',
                        validationRules: {
                            required: true,
                            min: 2,
                            max: 50,
                        },
                    },
                    currentAddressState: {
                        value: null,
                        mappedKey: 'state',
                        validationRules: {
                            required: true,
                            max: 50,
                        },
                    },
                    currentAddressCity: {
                        value: null,
                        mappedKey: 'city',
                        validationRules: {
                            required: true,
                            max: 50,
                        },
                    },
                    currentAddressZipCode: {
                        value: null,
                        mappedKey: 'zipcode',
                        legacyMappedKey: 'zip_code',
                        validationRules: {
                            required: true,
                            max: 5,
                        },
                    },
                },
            },
            {
                name: 'coBorrowerAddress',
                hideContinue: true,
                stringOnSummary: true,
                selected: null,
                manual: false,
                supportsSearch: true,
                route: {
                    relativeUrl: `co-borrower/address`,
                    name: 'apply.address.coBorrowerAddress',
                    props: { addressType: 'coBorrower', formSection: 'coBorrower' },
                    meta: {
                        title: 'form.sections.coBorrower.address.heading',
                    },
                },
                fields: {
                    coBorrowerAddressBuildingNumber: {
                        value: null,
                        mappedKey: 'co_app_address2',
                        validationRules: {
                            max: 50,
                        },
                    },
                    coBorrowerAddressStreetAddress: {
                        value: null,
                        mappedKey: 'co_app_address1',
                        validationRules: {
                            required: true,
                            min: 2,
                            max: 50,
                        },
                    },
                    coBorrowerAddressState: {
                        value: null,
                        mappedKey: 'co_app_state',
                        validationRules: {
                            required: true,
                            max: 50,
                        },
                    },
                    coBorrowerAddressCity: {
                        value: null,
                        mappedKey: 'co_app_city',
                        validationRules: {
                            required: true,
                            max: 50,
                        },
                    },
                    coBorrowerAddressZipCode: {
                        value: null,
                        mappedKey: 'co_app_zipcode',
                        validationRules: {
                            required: true,
                            max: 5,
                        },
                    },
                },
            },
        ]
    },

    setup() {
        const store = useStore()

        return {
            store,
        }
    },

    created() {
        this.emitter.emit('stepLoaded')

        this.addressFields.ZipCode.validationValue = this.zipCodeValue
        this.addressFields.StreetAddress.validationValue = this.streetNameValue
        this.addressFields.BuildingNumber.validationValue = this.buildingNumberValue
        this.addressFields.City.validationValue = this.cityValue
        this.addressFields.State.validationValue = this.stateValue

        this.addressFields.ZipCode.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressZipCode`,
        )
        this.addressFields.StreetAddress.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressStreetAddress`,
        )
        this.addressFields.BuildingNumber.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressBuildingNumber`,
        )
        this.addressFields.City.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressCity`,
        )
        this.addressFields.State.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressState`,
        )

        this.emitter.all.delete('addressField:selected')
        this.emitter.on('addressField:selected', async () => {
            await this.clearFields()
        })
    },

    props: ['addressType', 'formSection'],

    data() {
        return {
            name: `${this.addressType}Address`,
            label: this.$t(`form.sections.address.${this.addressType}Address.label`),
            errorMessage: this.$t('form.fields.zipCode.errorMessage'),
            validationRules: {
                required: true,
            },
            tooltip: null,
            searchName: `${this.addressType}AddressStreetAddress`,
            searchLabel:
                this.addressType === 'coBorrower'
                    ? this.$t('form.fields.coBorrowerAddress.label')
                    : this.$t('form.fields.address.labels.streetAddress'),
            addressFields: {
                StreetAddress: {
                    name: `${this.addressType}AddressStreetAddress`,
                    label:
                        this.addressType === 'coBorrower'
                            ? this.$t('form.fields.coBorrowerAddress.label')
                            : this.$t('form.fields.address.labels.streetAddress'),
                    selectLabel: this.$t('form.fields.address.labels.streetAddressSelected'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.streetAddress.errorMessage'),
                    searchField: true,
                },
                BuildingNumber: {
                    name: `${this.addressType}AddressBuildingNumber`,
                    label: this.$t('form.fields.address.labels.buildingNumber'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.buildingNumber.errorMessage'),
                },
                City: {
                    name: `${this.addressType}AddressCity`,
                    label: this.$t('form.fields.city.label'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.city.errorMessage'),
                },
                State: {
                    name: `${this.addressType}AddressState`,
                    label: this.$t('form.fields.state.label'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.state.errorMessage'),
                    AutoCompleteField: true,
                },
                ZipCode: {
                    name: `${this.addressType}AddressZipCode`,
                    label: this.$t('form.fields.address.labels.zipCode'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.zipCode.errorMessage'),
                },
            },
            clearFieldSteps: [`${this.addressType}AddressDuration`],
        }
    },
    computed: {
        zipCodeValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressZipCode`)
        },
        buildingNumberValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressBuildingNumber`)
        },
        streetAddressValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressStreetAddress`)
        },
        cityValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressCity`)
        },
        stateValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressState`)
        },
    },
    watch: {
        zipCodeValue: function (value) {
            this.addressFields.ZipCode.validationValue = value
        },
        buildingNumberValue: function (value) {
            this.addressFields.BuildingNumber.validationValue = value
        },
        streetAddressValue: function (value) {
            this.addressFields.StreetAddress.validationValue = value
        },
        cityValue: function (value) {
            this.addressFields.City.validationValue = value
        },
        stateValue: function (value) {
            this.addressFields.State.validationValue = value
        },
    },
    methods: {
        async clearFields() {
            if (this.addressType === 'current') {
                await this.store.dispatch('clearSteps', this.clearFieldSteps)
            }
        },
    },
}
</script>
